import type { RecipientPickerCopy } from '../share/recipients/RecipientPicker';
import { MAX_TEAM_RECIPIENTS } from '../share/recipients/useTeamRecipients';
import {
  nameList,
  type SendCopy,
  type SendOutcome,
  type SendRecipient,
  type SendTone
} from '../share/sendOutcome';
import { VISIBILITY_COPY } from './visibilityCopy';

/**
 * DV-3563: what Make Visible & Notify adds to the Make Visible dialog. The
 * labels follow the story's AC verbatim.
 */
export const NOTIFY_COPY = {
  heading: 'Send Email Notification',
  description: 'Let the client team know this page is ready.',
  noteLabel: 'Note to Client',
  confirmNotify: 'Make Visible & Notify',
  noMatch: (query: string) => `No team members match “${query}”.`
} as const;

/** The sender is not on the team they are picking from. */
export const CLIENT_TEAM_PICKER_COPY: RecipientPickerCopy = {
  loading: 'Loading the client’s team…',
  loadFailed: 'Couldn’t load the client’s team.'
};

/**
 * Why the email cannot go as things stand, shown under the block:
 *  - `unavailable`: this environment cannot send the team email, or could
 *    not say whether it can. The box is unchecked and disabled.
 *  - `no-team`: nobody on the client's team can be emailed. Likewise.
 *  - `list-failed`: the team could not be read; the picker offers Try Again.
 *  - `none-picked`: the box is checked with nobody selected (AC).
 *  - `too-many`: more selected than one send takes, which an account of
 *    over 100 members opens with, since everyone starts selected.
 * The last three disable the confirm button until fixed or the email is off.
 */
export type NotifyBlock = 'unavailable' | 'no-team' | 'list-failed' | 'none-picked' | 'too-many';

export const NOTIFY_BLOCK_COPY: Record<NotifyBlock, string> = {
  unavailable: 'Emailing the client’s team isn’t available right now.',
  'no-team':
    'No one on the client’s team can receive email yet. Members appear once they’ve accepted their invite and verified their email.',
  'list-failed': 'Turn off the email to make the page visible without it.',
  'none-picked': 'Select at least one team member, or turn off the email.',
  'too-many': `Select ${MAX_TEAM_RECIPIENTS} team members or fewer, or turn off the email.`
};

/**
 * The dialog has closed by the time these show, and the page is visible, so
 * none of them says "try again": `RESEND_HINT` says where to, when a resend
 * could reach anyone.
 */
export const REVEAL_SEND_COPY: SendCopy = {
  // Not shown: the dialog closes on any answer, with the toast saying what
  // came of the email.
  sentLine: (who) => `Email sent to ${who}.`,
  sentToast: (_who, count) => `Email sent to ${count} team ${count === 1 ? 'member' : 'members'}.`,
  invalid: 'The client’s team list changed.',
  // asset-service counts the sender's recipients over a rolling hour, so a
  // resend now would be refused the same way: "later", and no hint after it.
  throttled: 'You’ve sent a lot of share emails. Send it from Share later, on the Internal Share tab.',
  unavailable: 'Couldn’t send right now.'
};

/** Internal Share sends the same email once the photoset is visible. */
export const RESEND_HINT = {
  /** Nobody was sent. */
  everyone: 'Send it from Share, on the Internal Share tab.',
  /** Named, so it never reads as the unconfirmed recipient named before it. */
  to: (who: string) => `Send to ${who} from Share, on the Internal Share tab.`
} as const;

/** The email a reveal sent, and who it was addressed to. */
export interface RevealEmail {
  outcome: SendOutcome;
  recipients: readonly SendRecipient[];
}

/**
 * The toast a confirmed reveal ends with: the visibility toast, then what
 * came of the email (AC: "Page Visible to Client. 3 of 5 emails sent."). A
 * shortfall goes on to name who was not sent, and a send refused as a whole
 * counts nobody sent before saying why: the dialog has closed, so the toast
 * is the only place left to say so. When a resend could reach someone, it
 * ends on where to send it from (`resendHint`). Anything short of everyone
 * sent is a warning, never an error: the page did become visible.
 */
export const revealToast = (email: RevealEmail | null): { tone: SendTone; message: string } => {
  if (email === null) return { tone: 'success', message: VISIBILITY_COPY.toastVisible };
  const { outcome, recipients } = email;
  if (outcome.allSent) {
    return { tone: 'success', message: `${VISIBILITY_COPY.toastVisible}. ${outcome.toast}` };
  }
  // An unconfirmed send may have reached some, so it is not counted as none.
  const nobodySent = outcome.failure !== null && outcome.failure !== 'unconfirmed';
  const count = recipients.length;
  const detail = nobodySent
    ? `0 of ${count} ${count === 1 ? 'email' : 'emails'} sent. ${outcome.inline}`
    : outcome.inline;
  const hint = resendHint(email);
  return {
    tone: 'warning',
    message: `${VISIBILITY_COPY.toastVisible}. ${detail}${hint === null ? '' : ` ${hint}`}`
  };
};

/**
 * Where to resend, only for what a later send can clear: recipients the
 * email service throttled, or a send refused as a whole because the team
 * list changed (400) or sharing could not run (503). Never for a recipient
 * HubSpot refused, a 429 (its copy already says "later"), a 409, or anyone
 * who may have the email.
 */
const resendHint = ({ outcome, recipients }: RevealEmail): string | null => {
  if (outcome.failure === 'invalid' || outcome.failure === 'unavailable') {
    return RESEND_HINT.everyone;
  }
  const throttled = recipients
    .filter((recipient) => outcome.rateLimited.includes(recipient.id))
    .map((recipient) => recipient.label);
  return throttled.length > 0 ? RESEND_HINT.to(nameList(throttled)) : null;
};
